import { Button, Cascader, DatePicker, Form, Input, InputNumber, Select, Space } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Dialog as ZhDialog } from "zhihao-ui-react";

import { scheduleService } from "@/api/services/scheduleService";
import { shipService } from "@/api/services/shipService";
import { waybillService } from "@/api/services/waybillService";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";
import { areaLib, type AreaNode } from "@/utils/areaLib";
import { showRequestError } from "@/utils/requestErrorMessage";
import { AddressAutocomplete, getSuggestionAreaCodes, getSuggestionGeo } from "@/views/components/AddressAutocomplete";
import type { AddressInfo, OrderVo, ScheduleForm, ScheduleVo, TransportOrderShip } from "@zhihaoscm/entity";
import { fmtNum } from "@zhihaoscm/utils";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

import "./TradeOrderScheduleCreateDialog.css";

interface ScheduleCreateValues {
    scheduleDate: dayjs.Dayjs;
    estimatedTon: number;
    transportOrderShipId?: string;
    shipId?: string;
    contacts?: string;
    cellphone?: string;
    areaCode?: string[];
    detailAddress?: string;
    geo?: { lat: number; lng: number };
}

interface SelectOption {
    value: string;
    label: string;
    shipId?: string;
    shipName?: string;
}

interface AreaOption {
    value: string;
    label: string;
    children?: AreaOption[];
}

export interface TradeOrderScheduleCreateDialogProps {
    open: boolean;
    currentDate: string;
    detail: OrderVo;
    /** Existing schedule switches the shared dialog from creation to Vue-compatible editing. */
    schedule?: ScheduleVo;
    onCancel: () => void;
    onSaved: () => void | Promise<void>;
}

const PHONE_REGEXP = /^(((13[0-9])|(15[0-9])|(16[0-9])|(17[3-8])|(18[0-9])|(19[0-9])|(14[5-7]))\d{8})$/;

/** 将旧 Vue 地区树转换成 Cascader 选项，继续由 areaLib 提供省市区数据。 */
function mapAreaOptions(items: AreaNode[]): AreaOption[] {
    return items.map(item => ({
        value: item.id,
        label: item.name,
        children: item.childs?.length ? mapAreaOptions(item.childs) : undefined
    }));
}

/** 交易订单日历加号的新增排期表单，字段和校验对应 Vue ScheduleDialog。 */
export function TradeOrderScheduleCreateDialog({
    open,
    currentDate,
    detail,
    schedule,
    onCancel,
    onSaved
}: TradeOrderScheduleCreateDialogProps) {
    const [form] = Form.useForm<ScheduleCreateValues>();
    const [submitting, setSubmitting] = useState(false);
    const [waybillOptions, setWaybillOptions] = useState<SelectOption[]>([]);
    const [waybillKeyword, setWaybillKeyword] = useState("");
    const [shipOptions, setShipOptions] = useState<SelectOption[]>([]);
    // 仅在船舶搜索有关键词时展示结果空态。
    const [shipKeyword, setShipKeyword] = useState("");
    const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);
    const selectedWaybillId = Form.useWatch("transportOrderShipId", form);
    const selectedAreaCode = Form.useWatch("areaCode", form);
    const contacts = Form.useWatch("contacts", form) || "";
    // When editing, the current row's tons remain part of the editable order balance.
    const availableTon = Number(detail.notScheduledTon || 0) + Number(schedule?.schedule?.estimatedTon || 0);

    const fieldRules = useMemo(
        () => ({
            estimatedTon: [
                { required: true, message: "请输入预估提货吨数" },
                {
                    validator: (_: unknown, value?: number) => {
                        if (value == null) return Promise.resolve();
                        if (!Number.isInteger(value) || value <= 0 || value > 10000000) {
                            return Promise.reject(new Error("请输入0-10000000的正整数"));
                        }
                        if (value > availableTon) return Promise.reject(new Error("超出可排期吨数"));
                        return Promise.resolve();
                    }
                }
            ],
            cellphone: [
                {
                    validator: (_: unknown, value?: string) =>
                        !value || PHONE_REGEXP.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error("请输入正确的手机号"))
                }
            ]
        }),
        [availableTon]
    );

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        const currentSchedule = schedule?.schedule;
        const addressInfo = currentSchedule?.addressInfo;
        form.setFieldsValue({
            scheduleDate: dayjs(currentDate || currentSchedule?.scheduleDate),
            estimatedTon: currentSchedule?.estimatedTon,
            transportOrderShipId: currentSchedule?.transportOrderShipId || undefined,
            shipId: currentSchedule?.shipId || undefined,
            contacts: addressInfo?.contacts || "",
            cellphone: addressInfo?.cellphone || "",
            areaCode: addressInfo
                ? [addressInfo.province, addressInfo.city, addressInfo.district].filter(Boolean)
                : [],
            detailAddress: addressInfo?.detailAddress || "",
            geo: addressInfo?.geo
        });
        const waybill = schedule?.transportOrderShip;
        setWaybillOptions(
            waybill?.id
                ? [{
                      value: String(waybill.id),
                      label: `${waybill.id} | ${WAYBILL_STATE.getIntroByCode(Number(waybill.state)) || "--"} | ${waybill.goodsType ?? "--"} | ${waybill.ton ?? "--"}`,
                      shipId: currentSchedule?.shipId,
                      shipName: currentSchedule?.shipInfo?.cnname || currentSchedule?.shipInfo?.name
                  }]
                : []
        );
        setWaybillKeyword("");
        setShipOptions(
            currentSchedule?.shipId
                ? [{
                      value: String(currentSchedule.shipId),
                      label: currentSchedule.shipInfo?.cnname || currentSchedule.shipInfo?.name || String(currentSchedule.shipId)
                  }]
                : []
        );
        setShipKeyword("");
        // 地区字典异步加载后刷新级联选项，避免首次打开弹窗时地区为空。
        void areaLib.ready.then(() => setAreaOptions(mapAreaOptions(areaLib.getAreaList())));
    }, [currentDate, form, open, schedule]);

    /** 远程检索未绑定其他排期的运单，选中后自动带出关联船舶。 */
    const searchWaybills = async (keyword: string) => {
        const normalizedKeyword = keyword.trim();
        setWaybillKeyword(normalizedKeyword);
        if (!normalizedKeyword) {
            setWaybillOptions([]);
            return;
        }
        try {
            const rows = await waybillService.getSelector({ keyword: normalizedKeyword, excludeScheduled: true });
            setWaybillOptions(
                rows.map((row: TransportOrderShip) => ({
                    value: String(row.id),
                    label: `${row.id} | ${WAYBILL_STATE.getIntroByCode(Number(row.state)) || "--"} | ${row.goodsType ?? "--"} | ${row.ton ?? "--"}`,
                    shipId: row.shipId,
                    shipName: row.shipName
                }))
            );
        } catch (error) {
            showRequestError(error, "关联运单查询失败");
        }
    };

    const changeWaybill = (value?: string) => {
        const selected = waybillOptions.find(option => option.value === value);
        form.setFieldValue("shipId", selected?.shipId);
        setShipOptions(selected?.shipId ? [{ value: selected.shipId, label: selected.shipName || selected.shipId }] : []);
    };

    /** 没有关联运单时允许按船名或 AIS 号搜索船舶。 */
    const searchShips = async (keyword: string) => {
        const normalizedKeyword = keyword.trim();
        setShipKeyword(normalizedKeyword);
        if (!normalizedKeyword || selectedWaybillId) {
            setShipOptions([]);
            return;
        }
        try {
            const page = await shipService.getShipSelector({ keyword: normalizedKeyword });
            setShipOptions(
                (page.list || []).map(row => ({
                    value: String(row.id),
                    label: row.cnname || `${row.name || "--"} | ${row.id}`
                }))
            );
        } catch (error) {
            showRequestError(error, "船舶查询失败");
        }
    };

    /** 选择地址联想项时同步省市区和经纬度，保持 Vue 地址表单行为。 */
    const selectAddress = (suggestion: Parameters<NonNullable<React.ComponentProps<typeof AddressAutocomplete>["onSelectSuggestion"]>>[0]) => {
        const areaCodes = getSuggestionAreaCodes(suggestion);
        const geo = getSuggestionGeo(suggestion);
        if (!selectedAreaCode?.[2] && areaCodes.length) form.setFieldValue("areaCode", areaCodes);
        if (geo) form.setFieldValue("geo", { lat: geo.lat, lng: geo.lng });
    };

    /** 校验 Vue 的必填字段和吨数上限后按 ScheduleForm 契约提交。 */
    const submit = async () => {
        let values: ScheduleCreateValues;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSubmitting(true);
        try {
            const [province = "", city = "", district = ""] = values.areaCode || [];
            const addressInfo: AddressInfo = {
                contacts: values.contacts || "",
                cellphone: values.cellphone || "",
                areaCode: district,
                province,
                city,
                district,
                detailAddress: values.detailAddress || "",
                geo: values.geo || { lat: 0, lng: 0 }
            };
            const payload: ScheduleForm = {
                productId: String(detail.order.productId || ""),
                orderId: String(detail.order.id),
                scheduleDate: `${values.scheduleDate.format("YYYY-MM-DD")}T00:00:00`,
                estimatedTon: Number(values.estimatedTon),
                transportOrderShipId: values.transportOrderShipId || "",
                shipId: values.shipId || "",
                addressInfo
            } as ScheduleForm;
            if (schedule?.schedule?.id) {
                await scheduleService.update(String(schedule.schedule.id), { ...payload, id: String(schedule.schedule.id) });
                notifyOperationSuccess("修改排期成功");
            } else {
                await scheduleService.add(payload);
                notifyOperationSuccess("新增排期成功");
            }
            onCancel();
            await onSaved();
        } catch (error) {
            showRequestError(error, schedule?.schedule?.id ? "修改排期失败" : "新增排期失败");
        } finally {
            setSubmitting(false);
        }
    };

    const actions = (
        <Space>
            <Button disabled={submitting} onClick={onCancel}>
                取消
            </Button>
            <Button loading={submitting} type="primary" onClick={() => void submit()}>
                确认
            </Button>
        </Space>
    );

    return (
        <ZhDialog
            modalClass="trade-order-schedule-create-dialog"
            open={open}
            title={schedule?.schedule?.id ? "修改排期" : "新增排期"}
            width={920}
            onCancel={onCancel}
            footer={actions}
        >
            <div className="trade-order-schedule-create-dialog__section-title">排期信息</div>
            <Form<ScheduleCreateValues>
                className="trade-order-schedule-create-dialog__form"
                form={form}
                labelAlign="right"
                labelCol={{ flex: "112px" }}
                wrapperCol={{ flex: 1 }}
            >
                <Form.Item label="排期日期" name="scheduleDate" rules={[{ required: true, message: "请选择排期日期" }]}>
                    <DatePicker
                        aria-label="排期日期"
                        className="w-full"
                        disabledDate={date => date.isBefore(dayjs().startOf("day"), "day")}
                        format="YYYY-MM-DD"
                        prefix={<CalendarOutlined />}
                        placeholder="请选择排期日期"
                        suffixIcon={null}
                    />
                </Form.Item>
                <div className="trade-order-schedule-create-dialog__summary">
                    <div><span>采购提货吨数</span><strong>{fmtNum(detail.order.ton)}吨</strong></div>
                    <div><span>该订单未排期吨数</span><strong>{fmtNum(detail.notScheduledTon)}吨</strong></div>
                    <div><span>该订单已排期吨数</span><strong>{fmtNum(detail.scheduledTon)}吨</strong></div>
                </div>
                <Form.Item
                    className="trade-order-schedule-create-dialog__estimated-ton-item"
                    label="预估提货吨数"
                    name="estimatedTon"
                    rules={fieldRules.estimatedTon}
                >
                    <InputNumber aria-label="预估提货吨数" className="w-full" max={10000000} min={1} precision={0} suffix="吨" placeholder="请输入预估提货吨数" />
                </Form.Item>
                {/* Vue 版在吨数输入后保留第二列空位，关联运单从下一行开始。 */}
                <div aria-hidden="true" className="trade-order-schedule-create-dialog__form-spacer" />
                <Form.Item label="关联运单" name="transportOrderShipId">
                    <Select
                        allowClear
                        filterOption={false}
                        notFoundContent={waybillKeyword ? undefined : null}
                        options={waybillOptions}
                        placeholder="请选择关联运单"
                        showSearch
                        onChange={value => {
                            setWaybillKeyword("");
                            changeWaybill(value);
                        }}
                        onSearch={keyword => void searchWaybills(keyword)}
                    />
                </Form.Item>
                <Form.Item label="船舶" name="shipId">
                    <Select allowClear disabled={Boolean(selectedWaybillId)} filterOption={false} notFoundContent={shipKeyword ? undefined : null} options={shipOptions} placeholder="请输入船舶名称或AIS号搜索" showSearch onSearch={keyword => void searchShips(keyword)} />
                </Form.Item>
                <Form.Item label="收货人" name="contacts">
                    <Input maxLength={32} placeholder="请输入收货人" suffix={<span className="trade-order-schedule-create-dialog__count">{contacts.length} / 32</span>} />
                </Form.Item>
                <Form.Item label="收货人手机号" name="cellphone" rules={fieldRules.cellphone}>
                    <Input placeholder="请输入收货人手机号" />
                </Form.Item>
                <Form.Item label="收货地址" name="areaCode">
                    <Cascader options={areaOptions} placeholder="请选择" showSearch />
                </Form.Item>
                <Form.Item
                    className="trade-order-schedule-create-dialog__detail-address"
                    labelCol={{ flex: "0px" }}
                    name="detailAddress"
                >
                    <AddressAutocomplete aria-label="详细地址" regionCode={selectedAreaCode?.[2] || ""} onSelectSuggestion={selectAddress} />
                </Form.Item>
            </Form>
        </ZhDialog>
    );
}

export default TradeOrderScheduleCreateDialog;
